'use client';

import React, { FC, useEffect, useState } from 'react';
import styles from './ForgotPassword.module.scss';
import { AuthCard } from '@/modules/auth/components/AuthCard';
import { revalidateLogic, useForm } from '@tanstack/react-form';
import { Banner, BannerProps } from '@/components/atoms/Banner';
import { Button, ButtonLink, ButtonVariants } from '@/components/atoms/Button';
import { Typographies, Typography } from '@/components/atoms/Typography';
import { Icon } from '@/components/atoms/Icon';
import { TextField } from '@/components/molecules/TextField';
import { useSearchParams } from 'next/navigation';
import { ROUTES } from '@/routes/constants';
import { emailValidation } from '@/modules/auth/utils/validation';
import Link from 'next/link';
import { COMPANY_EMAIL } from '@/constants/common';
import { useForgotPassword } from '@/modules/auth/queries';
import { useAuthErrorMessages } from '@/modules/auth/hooks/useAuthErrorMessages';
import ResetPasswordImg from '@/assets/images/reset-password.webp';
import { extractErrorMessage } from '@/utils/error';
import { Trans, useTranslation } from 'react-i18next';

const ForgotPassword: FC = () => {
  const { t } = useTranslation();

  const [bannerMessage, setBannerMessage] = useState<BannerProps | null>(null);
  const [emailSent, setEmailSent] = useState('');

  const searchParams = useSearchParams();
  const { mutateAsync: forgotPassword, reset } = useForgotPassword();
  const getAuthErrorMessage = useAuthErrorMessages();

  const form = useForm({
    defaultValues: {
      email: '',
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    onSubmit: async ({ value }) => {
      try {
        reset();
        setBannerMessage(null);
        await forgotPassword(value.email);
        setEmailSent(value.email);
      } catch (error: unknown) {
        const errorMessage = extractErrorMessage(error);
        setBannerMessage(getAuthErrorMessage(errorMessage));
      }
    },
  });

  useEffect(() => {
    const errorCode = searchParams.get('error');

    if (errorCode) {
      setBannerMessage(getAuthErrorMessage(errorCode));
      const newUrl = window.location.pathname;
      window.history.replaceState(null, '', newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = () => {
    form.handleSubmit();

    setTimeout(() => {
      setBannerMessage({
        title: t('We sent a new reset email'),
        text: t('Please check your inbox'),
        type: 'success',
      });
    }, 100);
  };

  return (
    <AuthCard
      title={emailSent ? t('Check your email') : t('Reset your password')}
      quote={
        <Trans i18nKey="forgot_password.quote" components={{ br: <br /> }}>
          He who seeks truth <br /> will find it
        </Trans>
      }
      img={ResetPasswordImg}
      message={
        bannerMessage ? (
          <Banner type={bannerMessage.type} title={bannerMessage.title} text={bannerMessage.text} />
        ) : null
      }
      FormComponent={() => (
        <>
          {emailSent ? (
            <Typography variant={Typographies.BODY_1_REGULAR} className={styles.instructions}>
              {/* TODO: Revisit Trans usage here — currently relies on defaults + components. Verify final i18n approach (children vs i18nKey) and simplify if possible. */}
              <Trans
                i18nKey="forgot_password.reset_instructions"
                values={{ email: emailSent }}
                components={{
                  br: <br />,
                  resend: <span onClick={handleResend} />,
                  changeEmail: <span onClick={() => setEmailSent('')} />,
                }}
                defaults="An email with instructions on how to reset your password was sent to {{email}}<br /><br />If you do not receive the email within 5 minutes, check your spam folder <resend>resend</resend> or <changeEmail>try a different email address.</changeEmail>"
              />
            </Typography>
          ) : (
            <>
              <Typography variant={Typographies.BODY_1_REGULAR} className={styles.description}>
                {t("Enter your email address and we'll send you a link to reset your password.")}
              </Typography>

              <form
                className={styles.form}
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.handleSubmit();
                }}
              >
                <form.Field
                  name="email"
                  validators={{
                    onDynamic: ({ value }) => emailValidation(value, t),
                  }}
                >
                  {(field) => (
                    <TextField
                      label="Email"
                      placeholder={t('Enter your email')}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={field.state.meta.errors.length ? field.state.meta.errors[0] : ''}
                    />
                  )}
                </form.Field>

                <form.Subscribe selector={(state) => state}>
                  {(state) => (
                    <Button
                      variant={ButtonVariants.ACCENT}
                      type="submit"
                      size="big"
                      isFullWidth
                      disabled={!(state.isTouched && state.isValid && Object.values(state.values).every(Boolean))}
                      isLoading={state.isSubmitting}
                    >
                      {t('Send reset instructions')}
                    </Button>
                  )}
                </form.Subscribe>

                <ButtonLink
                  variant={ButtonVariants.SECONDARY}
                  size="big"
                  isFullWidth
                  href={ROUTES.SignIn}
                  StartIcon={() => <Icon name="navigation/arrow-left/16" size={16} />}
                >
                  {t('Back to Sign In')}
                </ButtonLink>
              </form>

              <Typography variant={Typographies.CAPTION_REGULAR} className={styles.support}>
                {t('For assistance, please contact our support team at')}{' '}
                <Link href={`mailto:${COMPANY_EMAIL}`}>
                  <span>{COMPANY_EMAIL}</span>
                </Link>
              </Typography>
            </>
          )}
        </>
      )}
    />
  );
};

export { ForgotPassword };
